/**
 * Copyright © 2026 Adnuntius AS.
 */

export class CreativeViewer {
  constructor(private readonly hostDocument: Document) {
  }

  viewCreative(container, previewData): void {
    if (!container || !previewData || !previewData.creativeContent) {
      return;
    }

    // Work on a local copy so toggling allowScript can remove preview-only guards.
    let creativeContent = previewData.creativeContent;
    if (previewData.dataLang) {
      creativeContent = this.injectLanguageIntoContent(creativeContent, previewData.dataLang);
    }

    const insertIndex = creativeContent.indexOf("</head>");
    if (insertIndex > -1 && !previewData.allowScript) {
      const styleSheet = "<style>a { pointer-events: none !important; }</style>";
      creativeContent = creativeContent.substring(0, insertIndex) + styleSheet + creativeContent.substring(insertIndex);
    }

    const divElement: HTMLElement = container;
    divElement.id = 'appPreviewIdContainer';

    const existingIframe = divElement.querySelector("iframe");
    if (existingIframe) {
      existingIframe.remove();
    }

    const ifr = this.hostDocument.createElement('iframe') as HTMLIFrameElement;
    (ifr as any).allowTransparency = true;

    let transformStyle = "";
    const previewId = "appPreviewId";
    if (previewData.maxWidth && previewData.maxHeight && (previewData.maxWidth < previewData.creativeWidth || previewData.maxHeight < previewData.creativeHeight)) {
      divElement.setAttribute('style', "width: " + previewData.maxWidth + "px;height:" + previewData.maxHeight + "px;");
      const width = previewData.creativeWidth,
        height = previewData.creativeHeight,
        ratio = width > height ? previewData.maxWidth / width : previewData.maxHeight / height;
      transformStyle = "zoom:(" + ratio + ");-moz-transform: scale(" + ratio + "); -moz-transform-origin: 0 0; -o-transform: scale(" + ratio + "); -o-transform-origin: 0 0; -webkit-transform: scale(" + ratio + "); -webkit-transform-origin: 0 0;";
    }

    ifr.setAttribute('style', previewData.showBorder ? 'outline: 3px solid #bbb;' : '' + 'display: block;margin: 0;borderWidth: 0; padding: 0;' + transformStyle);
    ifr.setAttribute('scrolling', 'no');
    ifr.setAttribute('id', previewId);
    if (previewData.dataLangAttribute) {
      ifr.setAttribute('data-lang', previewData.dataLangAttribute);
    }
    if (previewData.creativeWidth) {
      ifr.setAttribute('width', previewData.creativeWidth);
    }
    if (previewData.creativeHeight) {
      ifr.setAttribute('height', previewData.creativeHeight);
    }
    ifr.frameBorder = "0";

    const sandboxTheIframe = !previewData.notSandbox && "sandbox" in ifr && "srcdoc" in ifr;
    if (!sandboxTheIframe && (!previewData.allowScript || previewData.notSandbox)) {
      ifr.setAttribute('src', '#?ifrId=' + previewId);
    } else {
      ifr.setAttribute("sandbox", "allow-same-origin");
    }

    if (sandboxTheIframe) {
      ifr.srcdoc = creativeContent;
      ifr.setAttribute("sandbox", "allow-popups allow-pointer-lock allow-top-navigation allow-forms allow-modals allow-orientation-lock allow-popups-to-escape-sandbox allow-presentation allow-top-navigation-by-user-activation");

      if (previewData.allowScript) {
        ifr.setAttribute('sandbox', (ifr.getAttribute('sandbox') || '') + ' allow-scripts');
      }
      divElement.append(ifr);
    } else {
      divElement.append(ifr);
      const iframeDoc = ifr.contentDocument || ifr.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(creativeContent);
      iframeDoc.close();

      const bodyElement = iframeDoc.body;
      if (bodyElement) {
        bodyElement.setAttribute('style', 'margin: 0; padding: 0;');
      }
    }
  }

  private injectLanguageIntoContent(content: string, dataLang: string): string {
    const safeLang = this.escapeHtmlAttribute(dataLang);

    let updatedContent = content;

    if (/<html[\s>]/i.test(updatedContent)) {
      updatedContent = updatedContent.replace(/<html([^>]*)>/i, (_match, attrs) => {
        const cleanedAttrs = attrs
          .replace(/\slang=(['"]).*?\1/i, '')
          .replace(/\sdata-lang=(['"]).*?\1/i, '');

        return `<html${cleanedAttrs} lang="${safeLang}" data-lang="${safeLang}">`;
      });
    }

    if (/<body[\s>]/i.test(updatedContent)) {
      updatedContent = updatedContent.replace(/<body([^>]*)>/i, (_match, attrs) => {
        const cleanedAttrs = attrs.replace(/\sdata-lang=(['"]).*?\1/i, '');
        return `<body${cleanedAttrs} data-lang="${safeLang}">`;
      });
    }

    return updatedContent;
  }

  private escapeHtmlAttribute(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
